<nz-card class="card">
  <h5>Issue Overview:</h5>
  <p *ngIf="enclosureBk">
    <b
      >{{ storageLevelV2.String | levelIdentifierToLevelName | titlecase }} Location:
      {{ enclosureBk | componentNameTranslation }} &rarr; {{ componentBk | componentNameTranslation }}</b
    >
  </p>
  <p>
    A weak {{ storageLevelV2.Cell | levelIdentifierToLevelName }} has been identified in
    <b>{{ componentBk | componentNameTranslation }}</b>
    <span *ngIf="getCellPosition()"
      >, specifically <b>{{ getCellPosition() }}</b></span
    >. This {{ storageLevelV2.Cell | levelIdentifierToLevelName }} is underperforming relative to its peers in the
    {{ storageLevelV2.String | levelIdentifierToLevelName }}, which introduces energy imbalance and reduces the total amount of
    usable energy available from the {{ storageLevelV2.System | levelIdentifierToLevelName }}. The energy associated with this
    {{ storageLevelV2.Cell | levelIdentifierToLevelName }} is currently classified as recoverable, but only if corrective measures
    are taken.
  </p>
  <p>Weak {{ storageLevelV2.Cell | levelIdentifierToLevelName }} behavior may arise from a combination of:</p>

  <ul class="list-disc">
    <li><b>Age-related capacity fade,</b> often due to cycle life or calendar aging.</li>
    <li><b>Elevated internal resistance,</b> leading to disproportionate heating and voltage drops.</li>
    <li>
      <b>Thermal gradients or persistent SoC imbalances,</b> amplifying individual
      {{ storageLevelV2.Cell | levelIdentifierToLevelName }} degradation compared with the other
      {{ storageLevelV2.Cell | storageLevelToCustomerLevelName: false }}.
    </li>
    <li><b>Manufacturing variances</b> or historical overcharge/over-discharge incidents.</li>
  </ul>
  <br />
  <p>
    If unresolved, weak {{ storageLevelV2.Cell | storageLevelToCustomerLevelName: false }} can limit the overall
    {{ storageLevelV2.String | levelIdentifierToLevelName }} performance, trigger BMS derating actions, or accelerate the
    degradation of neighboring {{ storageLevelV2.Cell | storageLevelToCustomerLevelName: false }} due to uneven current
    distribution.
  </p>
</nz-card>
<nz-card class="card">
  <h5>Why This Matters for You:</h5>
  <ul class="list-disc">
    <li><b>Improved financial performance</b> by recovering lost capacity and minimizing downtime.</li>
    <li><b>Trigger faulty component replacements early on</b> to raise a claim during the warranty period.</li>
  </ul>
  <br />
  <p>
    By acting now, you ensure your asset operates closer to its nameplate performance, and that any corrective measures required
    from third parties are supported by data and evidence.
  </p>
</nz-card>
@if (hasSiblingEffect) {
  <nz-card class="card">
    <h5>Sibling effect</h5>
    <p>
      The sibling effect occurs within this
      {{ storageLevelV2.DCCombiner | levelIdentifierToLevelName }}. This means, that the usable energy of this
      {{ storageLevelV2.DCCombiner | levelIdentifierToLevelName }} is determined by its worst
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }}. The usable energy of the
      {{ storageLevelV2.DCCombiner | levelIdentifierToLevelName }} is calculated by the usable energy of the worst
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }} multiplied by the number of sibling
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }}s within this
      {{ storageLevelV2.DCCombiner | levelIdentifierToLevelName }} (and not by the sum of usable energy of individual
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }}s).
    </p>
    <p>
      TWAICE recommends the "quick fix" to take the worst
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }} offline if the remaining usable energy of the worst
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }} is less than the energy that would be recovered in the
      whole {{ storageLevelV2.DCCombiner | levelIdentifierToLevelName }} by taking the worst
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }} offline plus an additional {{ quickFixBufferPercent }}%
      buffer.
    </p>
    <p>
      In this case, the difference is <b>below</b> the buffer of {{ quickFixBufferPercent }}%. We <b>don't recommend</b>
      the quick fix.
    </p>
  </nz-card>
}
<nz-card class="card">
  <h5>Recommendations:</h5>
  <div [ngSwitch]="recommendation">
    <p *ngSwitchCase="Recommendation.Monitor">
      <b>Routine monitoring is recommended</b> to track the weak cell's behaviour and assess the risk of further imbalance
      development. The performance limitation is currently limited, but can develop into a noticeable issue if not monitored. Keep
      an eye on the component.
    </p>
    <p *ngSwitchCase="Recommendation.Inspect">
      <b>Targeted inspection and diagnostic verification recommended</b> to validate whether the weak cell's imbalance is
      beginning to constrain component performance and requires proactive maintenance. The performance limitation due to the
      component is not yet significant enough to justify a immediate replacement steps. Nevertheless, with underperformance and
      imbalances increasing, the performance limitation could worsen.
    </p>
    <div *ngSwitchDefault>
      <p>
        Start with a technical investigation to determine whether this weak
        {{ storageLevelV2.Cell | levelIdentifierToLevelName }} requires immediate intervention or can be monitored further.
        Recommended steps include:
      </p>
      <ul class="list-disc">
        <li>
          <b>Monitor the {{ storageLevelV2.Cell | levelIdentifierToLevelName }} over time</b> for voltage limit violations, SoC
          divergences compared to other {{ storageLevelV2.Cell | storageLevelToCustomerLevelName: false }}, and elevated
          self-discharge.
        </li>
        <li>
          <b>Analyze past behavior</b> using the Data Explorer to identify when
          {{ storageLevelV2.StringModule | levelIdentifierToLevelName }} or
          {{ storageLevelV2.Cell | levelIdentifierToLevelName }} ID first highlighted the
          {{ storageLevelV2.Cell | levelIdentifierToLevelName }} continuously.
        </li>
        <li>
          <b>Consult with your O&M provider or manufacturer</b> to evaluate if balancing, thermal improvements, or
          {{ storageLevelV2.Cell | levelIdentifierToLevelName }}/{{ storageLevelV2.StringModule | levelIdentifierToLevelName }}
          replacement is recommended.
        </li>
      </ul>
    </div>
  </div>
</nz-card>

<nz-card class="card">
  <h5>Documenting the issue:</h5>
  <p>To facilitate a timely and informed resolution, take the following actions:</p>
  <ol class="list-decimal">
    <li>
      <b>Export CSV data</b> for {{ componentBk | componentNameTranslation }} to include
      {{ storageLevelV2.Cell | levelIdentifierToLevelName }}-level performance indicators such as voltage (spread), SoC (spread),
      and {{ storageLevelV2.StringModule | levelIdentifierToLevelName }} or
      {{ storageLevelV2.Cell | levelIdentifierToLevelName }} ID signals over time.
    </li>
    <li>
      <b>Capture screenshots</b> from the Data Explorer showing relevant trends, including the evolution of
      {{ storageLevelV2.Cell | levelIdentifierToLevelName }} voltage deviation and any related voltage limit crossings.
    </li>
    <li>
      <b>Prepare a short summary report</b> combining your observations with the data in the platform. This will provide O&M teams
      or {{ storageLevelV2.System | levelIdentifierToLevelName }} partners with a clear foundation for action planning or
      escalation.
    </li>
  </ol>
  <br />
  <p>This documentation ensures traceability and provides a clear timeline of when the issue emerged and how it evolved.</p>
</nz-card>

<nz-card class="card">
  <h5>Guidance for Collaboration with Service and Hardware Providers:</h5>
  <p>To ensure constructive and efficient collaboration with your internal and external stakeholders:</p>
  <ul class="list-disc">
    <li>Share the <b>exported CSV and screenshots</b> showing the underperformance and timeline of degradation.</li>
    <li>
      Highlight any <b>deviation from typical {{ storageLevelV2.Cell | levelIdentifierToLevelName }} behavior,</b> especially if
      other {{ storageLevelV2.Cell | storageLevelToCustomerLevelName: false }} in the
      {{ storageLevelV2.String | levelIdentifierToLevelName }} are stable.
    </li>
    <li>
      Ask for clarification on <b>maintenance procedures, balancing protocols,</b> or eligibility for
      <b>corrective support under warranty.</b>
    </li>
    <li>
      Reference relevant entries from your <b>OEM documentation,</b> if available, to align on known failure modes and repair
      procedures.
    </li>
  </ul>
</nz-card>
